'use client'

import { useState } from 'react'

import { ApplicationCoverLetterView } from '@/components/employer/application-cover-letter-view'
import { EvaluationAnalysisDetail } from '@/components/evaluation/evaluation-analysis-detail'
import { EvaluationAnalysisView } from '@/components/evaluation/evaluation-analysis-view'
import { MatchDetailShell } from '@/components/evaluation/match-detail-shell'
import { PersonProfileStandingView } from '@/components/profile/person-profile-standing-view'
import { personDisplayName } from '@/components/profile/person-profile-read-view'
import { EntityDetailSkeleton } from '@/components/layout/entity-detail-skeleton'
import {
  ArtifactCanvasBody,
  ArtifactCanvasFooterBar,
} from '@/components/artifact/artifact-canvas-layout'
import { Badge } from '@/components/ui/badge'
import type { EvaluationAnalysisPayload } from '@/hooks/use-evaluation-analysis'
import { useProfileById } from '@/hooks/use-profile-by-id'
import type { PersonMatchDetailTab } from '@/lib/match-detail-paths'

export type RubricSamplePreviewMeta = {
  subject_label?: string
  profile_id?: string | null
  opportunity_id?: string | null
  opportunity_title?: string | null
  application_id?: string | null
  evaluation_id?: string | null
  methodology_id?: string
  condition_id?: string | null
}

type RubricSampleMatchCanvasProps = {
  payload: EvaluationAnalysisPayload
  previewMeta?: RubricSamplePreviewMeta
  subjectLabel?: string
  validationMessage?: string | null
  isSampleRun?: boolean
}

export function RubricSampleMatchCanvas({
  payload,
  previewMeta,
  subjectLabel,
  validationMessage,
  isSampleRun = false,
}: RubricSampleMatchCanvasProps) {
  const methodology = previewMeta?.methodology_id || payload.methodology_id || 'employment_match'
  const isEmployment = methodology === 'employment_match'
  const profileId = previewMeta?.profile_id || null
  const evaluationId = previewMeta?.evaluation_id || null
  const applicationId = previewMeta?.application_id || null
  const { profile, isLoading: profileLoading } = useProfileById(isEmployment ? profileId : null)
  const [activeTab, setActiveTab] = useState<PersonMatchDetailTab>('analysis')

  const label =
    subjectLabel ||
    previewMeta?.subject_label ||
    (profile ? personDisplayName(profile) : 'Sample subject')

  if (!isEmployment) {
    return (
      <ArtifactCanvasBody className="flex h-full min-h-0 flex-col space-y-0 p-0">
        <div className="min-h-0 flex-1 space-y-4 overflow-y-auto p-6">
          <div className="flex flex-wrap items-center gap-2">
            <Badge variant="outline">{isSampleRun ? 'Sample run' : 'Draft shape'}</Badge>
            <p className="text-sm text-muted-foreground">
              Condition self-management sample report (non-diagnostic).
            </p>
          </div>
          <EvaluationAnalysisDetail payload={payload} subjectLabel={label} />
        </div>
        <ArtifactCanvasFooterBar
          footerClassName="shrink-0"
          leading={
            <p className="text-sm text-muted-foreground">
              Create a health scoring sample on Simulation if sample subjects are missing.
            </p>
          }
          actions={
            validationMessage ? (
              <p className="text-sm text-warning">{validationMessage}</p>
            ) : (
              <p className="text-sm text-muted-foreground">Draft validates.</p>
            )
          }
        />
      </ArtifactCanvasBody>
    )
  }

  if (profileLoading && profileId) {
    return <EntityDetailSkeleton variant="job" ariaLabel="Loading sample match" />
  }

  const analysisPanel = evaluationId ? (
    <EvaluationAnalysisView evaluationId={evaluationId} subjectLabel={label} />
  ) : (
    <EvaluationAnalysisDetail payload={payload} subjectLabel={label} />
  )

  const profilePanel = profile ? (
    <PersonProfileStandingView
      profile={profile}
      showIdentityName={false}
      showAvatar={false}
      skillsTitle="Skills (self-reported)"
      skillsDescription="Job-mapped skills are on the Analysis tab. This list is what they reported on their profile."
      className="pt-6 pb-6"
    />
  ) : (
    <p className="text-sm text-muted-foreground p-6">
      Profile details unavailable for this sample. Analysis scores still reflect the draft rubric.
    </p>
  )

  return (
    <ArtifactCanvasBody className="flex h-full min-h-0 flex-col space-y-0 p-0">
      <div className="min-h-0 flex-1 overflow-y-auto">
        <div className="px-6 pt-4 flex flex-wrap items-center gap-2">
          <Badge variant="outline">{isSampleRun ? 'Sample run' : 'Draft shape'}</Badge>
          <p className="text-sm text-muted-foreground">
            Person-match chrome for rubric QA
            {previewMeta?.opportunity_title ? ` · ${previewMeta.opportunity_title}` : ''}.
          </p>
        </div>
        <MatchDetailShell
          mode="person"
          title={label}
          description="Sample analysis against demo / Simulation subject"
          breadcrumbs={[
            { label: 'Platform' },
            { label: 'Rubrics' },
            { label: 'Sample analysis' },
          ]}
          activeTab={activeTab}
          onTabChange={setActiveTab}
          hasAnalysis
          hasCoverLetter={Boolean(applicationId)}
          analysisPanel={analysisPanel}
          letterPanel={
            applicationId ? (
              <ApplicationCoverLetterView
                applicationId={applicationId}
                candidateName={label}
              />
            ) : null
          }
          secondaryPanel={profilePanel}
        />
      </div>
      <ArtifactCanvasFooterBar
        footerClassName="shrink-0"
        leading={
          <p className="text-sm text-muted-foreground">
            Scores use the draft rubric. Publish to refresh live evaluations.
          </p>
        }
        actions={
          validationMessage ? (
            <p className="text-sm text-warning">{validationMessage}</p>
          ) : (
            <p className="text-sm text-muted-foreground">Draft validates.</p>
          )
        }
      />
    </ArtifactCanvasBody>
  )
}
